import type { DevicePosition } from './device-location';

const baseUrl = import.meta.env.VITE_API_URL as string;

if (!baseUrl) {
  throw new Error('VITE_API_URL is required');
}

export interface ApiErrorBody {
  error?: { code?: string };
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, code?: string) {
    super(code ?? `Request failed: ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const parseError = async (response: Response): Promise<ApiError> => {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return new ApiError(response.status, body.error?.code);
  } catch {
    return new ApiError(response.status);
  }
};

const authHeaders = (accessToken?: string): Record<string, string> =>
  accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

export const api = {
  get: async <T>(path: string, accessToken?: string, position?: DevicePosition): Promise<T> => {
    const query = position
      ? `${path}${path.includes('?') ? '&' : '?'}latitude=${encodeURIComponent(position.latitude)}&longitude=${encodeURIComponent(position.longitude)}&accuracyMetres=${encodeURIComponent(position.accuracyMetres)}`
      : path;
    const response = await fetch(`${baseUrl}${query}`, {
      cache: 'no-store',
      headers: {
        'X-Trinity-Client': 'checkin-web/0.1.0',
        ...authHeaders(accessToken),
      },
    });
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as T;
  },
  post: async <T>(
    path: string,
    body: unknown,
    accessToken?: string,
  ): Promise<T> => {
    const response = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        'X-Trinity-Client': 'checkin-web/0.1.0',
        ...authHeaders(accessToken),
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as T;
  },
};
